import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { 
  ArrowRight, 
  CheckCircle, 
  TrendingUp, 
  Package, 
  Shield, 
  Users,
  Calculator,
  Truck,
  Handshake,
  Store
} from "lucide-react";

const productTypes = [
  { id: "iphone", label: "iPhone" },
  { id: "macbook", label: "MacBook" },
  { id: "ipad", label: "iPad" },
  { id: "airpods", label: "AirPods" },
  { id: "watch", label: "Apple Watch" },
  { id: "accessories", label: "Accesorios" },
];

const volumeOptions = [
  { value: "1-10", label: "1-10 unidades/mes" },
  { value: "11-50", label: "11-50 unidades/mes" },
  { value: "51-100", label: "51-100 unidades/mes" },
  { value: "100+", label: "Más de 100 unidades/mes" },
];

const benefits = [
  {
    icon: Users,
    title: "Acceso a miles de compradores",
    description: "Llega a nuestra base de clientes activos que buscan productos Apple a buenos precios."
  },
  {
    icon: TrendingUp,
    title: "Ventas garantizadas",
    description: "El modelo de compra grupal asegura un mínimo de ventas antes de que envíes el producto."
  },
  {
    icon: Shield,
    title: "Sin riesgo de impagos",
    description: "BulkBytes gestiona todos los pagos. Tú recibes tu dinero cuando la venta se confirma."
  },
  {
    icon: Calculator,
    title: "Precios transparentes",
    description: "Conoce de antemano la comisión y calcula tus beneficios con nuestra calculadora."
  },
  {
    icon: Truck,
    title: "Logística simplificada",
    description: "Envía todo a nuestro almacén. Nosotros nos encargamos de la distribución final."
  },
  {
    icon: Handshake,
    title: "Soporte dedicado",
    description: "Un gestor de cuenta te ayudará a optimizar tus ventas y resolver cualquier duda."
  }
];

const steps = [
  {
    number: "01",
    title: "Solicita unirte",
    description: "Rellena el formulario con información sobre tu empresa y productos."
  },
  {
    number: "02",
    title: "Entrevista y KYC",
    description: "Nuestro equipo se pondrá en contacto para conocerte mejor y verificar tu identidad."
  },
  {
    number: "03",
    title: "Activa tu cuenta",
    description: "Una vez aprobado, accede a tu panel de partner y empieza a subir productos."
  },
  {
    number: "04",
    title: "Vende y cobra",
    description: "Nosotros publicamos, tú envías cuando hay venta confirmada y recibes tu pago."
  }
];

export default function PartnerLanding({ user, logout }) {
  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    website: "",
    product_types: [],
    estimated_monthly_volume: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleProductTypeChange = (productId, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        product_types: [...prev.product_types, productId]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        product_types: prev.product_types.filter(id => id !== productId)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.product_types.length === 0) {
      toast.error("Selecciona al menos un tipo de producto");
      return;
    }
    
    if (!formData.estimated_monthly_volume) {
      toast.error("Selecciona el volumen mensual estimado");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/partners/apply`, formData);
      setSubmitted(true);
      toast.success("Solicitud enviada correctamente");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar la solicitud");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} logout={logout} />

      {/* Hero */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 bg-gradient-to-br from-primary via-primary to-primary/90 text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
                <Store className="h-4 w-4" />
                <span className="text-sm font-medium">Programa de Partners</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Vende tu stock Apple con BulkBytes
              </h1>
              <p className="text-lg md:text-xl text-primary-foreground/80 mb-8">
                Únete a nuestra red de partners y accede a miles de compradores. 
                Sin riesgo de stock parado, sin gestión de envíos individuales.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="h-14 px-8 rounded-full bg-orange-500 text-white hover:bg-orange-600"
                  onClick={() => document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' })}
                  data-testid="partner-apply-btn"
                >
                  Solicitar acceso
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="h-14 px-8 rounded-full border-white/30 text-white hover:bg-white/10"
                  onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Cómo funciona
                </Button>
              </div>
            </div>
            <div className="hidden md:flex justify-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
                <div className="text-center mb-6">
                  <p className="text-sm text-primary-foreground/70 mb-2">Comisión desde</p>
                  <p className="text-5xl font-bold">10%</p>
                  <p className="text-sm text-primary-foreground/70 mt-2">sobre el precio de venta</p>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-400" />
                    <span className="text-sm">Sin costes de alta</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-400" />
                    <span className="text-sm">Sin cuotas mensuales</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-400" />
                    <span className="text-sm">Solo pagas cuando vendes</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              ¿Por qué ser Partner de BulkBytes?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Nos encargamos de todo lo complicado para que tú te centres en lo que mejor sabes hacer
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="p-6 bg-white rounded-2xl border border-border/40 hover:shadow-lg transition-shadow"
                data-testid={`partner-benefit-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <benefit.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Cómo funciona
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              En 4 sencillos pasos estarás vendiendo tu stock
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative" data-testid={`partner-step-${index}`}>
                <div className="text-6xl font-bold text-primary/10 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {step.number}
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {step.title}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {step.description}
                </p>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 -right-4 text-primary/20">
                    <ArrowRight className="h-8 w-8" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="application-form" className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Solicita ser Partner
            </h2>
            <p className="text-muted-foreground">
              Rellena el formulario y nos pondremos en contacto contigo en 24-48h
            </p>
          </div>

          {submitted ? (
            <div className="text-center py-12 bg-green-50 rounded-2xl border border-green-200" data-testid="application-success">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                ¡Solicitud enviada!
              </h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Gracias por tu interés en ser Partner de BulkBytes. 
                Nuestro equipo revisará tu solicitud y se pondrá en contacto contigo pronto.
              </p>
              <Link to="/">
                <Button className="mt-6">Volver al inicio</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6 bg-white p-8 rounded-2xl border shadow-sm" data-testid="partner-application-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Nombre de la empresa *</Label>
                  <Input
                    id="company_name"
                    required
                    value={formData.company_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder="Tu empresa S.L."
                    data-testid="partner-company-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_name">Nombre de contacto *</Label>
                  <Input
                    id="contact_name"
                    required
                    value={formData.contact_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                    placeholder="Juan García"
                    data-testid="partner-contact-name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="contacto@tuempresa.com"
                    data-testid="partner-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Teléfono *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+34 612 345 678"
                    data-testid="partner-phone"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">Sitio web (opcional)</Label>
                <Input
                  id="website"
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="https://tuempresa.com"
                  data-testid="partner-website"
                />
              </div>

              <div className="space-y-3">
                <Label>¿Qué productos vendes? *</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {productTypes.map((type) => (
                    <div key={type.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`product-${type.id}`}
                        checked={formData.product_types.includes(type.id)}
                        onCheckedChange={(checked) => handleProductTypeChange(type.id, checked)}
                        data-testid={`partner-product-${type.id}`}
                      />
                      <label
                        htmlFor={`product-${type.id}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {type.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Volumen mensual estimado *</Label>
                <Select
                  value={formData.estimated_monthly_volume}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, estimated_monthly_volume: value }))}
                >
                  <SelectTrigger data-testid="partner-volume">
                    <SelectValue placeholder="Selecciona un rango" />
                  </SelectTrigger>
                  <SelectContent>
                    {volumeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Mensaje adicional (opcional)</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Cuéntanos más sobre tu empresa, experiencia con productos Apple, etc."
                  rows={4}
                  data-testid="partner-message"
                />
              </div>

              <Button 
                type="submit" 
                size="lg" 
                className="w-full h-12"
                disabled={submitting}
                data-testid="partner-submit-btn"
              >
                {submitting ? "Enviando..." : "Enviar solicitud"}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                Al enviar este formulario, aceptas nuestra{" "}
                <Link to="/privacy" className="underline">Política de Privacidad</Link>
                {" "}y nuestros{" "}
                <Link to="/terms" className="underline">Términos y Condiciones</Link>.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            ¿Tienes preguntas?
          </h2>
          <p className="text-lg text-primary-foreground/80 mb-8">
            Contacta con nuestro equipo de partners para resolver cualquier duda
          </p>
          <a href="mailto:bulkbytes26@gmail.com">
            <Button 
              size="lg" 
              className="h-14 px-8 rounded-full bg-white text-primary hover:bg-gray-100"
            >
              bulkbytes26@gmail.com
            </Button>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-muted-foreground">
            © 2025 BulkBytes. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}
